import { ChatType } from '../types/monitor/ChatType';
import { MonitorType } from '../types/monitor/MonitorType';
import { t } from 'i18next';
import { canShowModel } from '../utils/utils';

export interface ChatTypeOptionItem {
  name: string;
  type: ChatType;
  icon?: string;
  locked?: boolean;
  separator?: boolean;
  disabled?: boolean;
}

const ALL_MODEL_DEFINITIONS: { modelKey: string; nameKey: string; type: ChatType; icon: string }[] = [
  { modelKey: 'GPT', nameKey: 'gptName', type: 'GPT', icon: 'ico--gpt-bck' },
  { modelKey: 'GEMINI', nameKey: 'geminiName', type: 'GEMINI', icon: 'ico--gemini-bck' },
  { modelKey: 'CLAUDE', nameKey: 'claudeName', type: 'CLAUDE', icon: 'ico--claude-bck' },
  { modelKey: 'DEEP_SEEK', nameKey: 'deepSeekName', type: 'DEEP_SEEK', icon: 'ico--deepseek-bck' },
  { modelKey: 'PERPLEXITY', nameKey: 'perplexityName', type: 'PERPLEXITY', icon: 'ico--perplexity-bck' },
  { modelKey: 'GROK', nameKey: 'grokSearchName', type: 'GROK_BROWSER', icon: 'ico--grok-bck' },
  {
    modelKey: 'COPILOT',
    nameKey: 'copilotSearchName',
    type: 'COPILOT_BROWSER',
    icon: 'ico--copilot-bck',
  },
  {
    modelKey: 'AI_OVERVIEW_NEW',
    nameKey: 'aiOverviewName',
    type: 'AI_OVERVIEW_NEW_BROWSER',
    icon: 'ico--ai-overview-bck',
  },
  { modelKey: 'AI_OVERVIEW', nameKey: 'aiModeName', type: 'AI_MODE_BROWSER', icon: 'ico--ai-mode' },
];

const DOMAINS_PER_MODEL_ORDER: ChatType[] = [
  'GPT',
  'PERPLEXITY',
  'GEMINI',
  'AI_MODE_BROWSER',
  'AI_OVERVIEW_NEW_BROWSER',
  'GROK_BROWSER',
  'COPILOT_BROWSER',
  'CLAUDE',
  'DEEP_SEEK',
];

export const domainsPerModelOptions = (features: MonitorType[]): ChatTypeOptionItem[] => {
  const all = chatTypeOptions(features).filter((opt) => opt.type !== 'ALL');
  return DOMAINS_PER_MODEL_ORDER.map((type) => all.find((opt) => opt.type === type)).filter(
    (opt): opt is ChatTypeOptionItem => !!opt
  );
};

export const chatTypeOptions = (features: MonitorType[]): ChatTypeOptionItem[] => {
  const options: ChatTypeOptionItem[] = [
    { name: t('all'), type: 'ALL', icon: 'ico--models ico--globe' },
  ];

  for (const def of ALL_MODEL_DEFINITIONS) {
    if (canShowModel(def.modelKey, features)) {
      options.push({ name: t(def.nameKey), type: def.type, icon: def.icon });
    }
  }

  return options;
};

export const allChatTypeOptions = (features: MonitorType[]): ChatTypeOptionItem[] => {
  const options: ChatTypeOptionItem[] = [
    { name: t('all'), type: 'ALL', icon: 'ico--models ico--globe' },
  ];

  const available: ChatTypeOptionItem[] = [];
  const locked: ChatTypeOptionItem[] = [];

  for (const def of ALL_MODEL_DEFINITIONS) {
    const item: ChatTypeOptionItem = { name: t(def.nameKey), type: def.type, icon: def.icon };
    if (canShowModel(def.modelKey, features)) {
      available.push(item);
    } else {
      locked.push({ ...item, locked: true, disabled: true });
    }
  }

  options.push(...available);

  if (locked.length > 0) {
    options.push({ name: '', type: '__SEPARATOR__' as ChatType, separator: true, disabled: true });
    options.push(...locked);
  }

  return options;
};

/**
 * Order of MonitorTypes matching the dropdown (chatTypeOptions): PERPLEXITY, GPT, GROK, AI_MODE, GEMINI, AI_OVERVIEW_NEW, COPILOT, CLAUDE, DEEP_SEEK.
 */
export const MONITOR_TYPE_DISPLAY_ORDER: MonitorType[] = [
  'PERPLEXITY_API',
  'PERPLEXITY_SEARCH_BROWSER',
  'GPT',
  'GPT_API',
  'GPT_API_BROWSER',
  'GPT_SEARCH_BROWSER',
  'GPT_API_SEARCH_WEB',
  'GROK_API',
  'GROK_SEARCH_BROWSER',
  'AI_MODE',
  'AI_OVERVIEW',
  'GEMINI_API',
  'GEMINI_API_BROWSER',
  'GEMINI_SEARCH_BROWSER',
  'AI_OVERVIEW_NEW',
  'COPILOT_SEARCH_BROWSER',
  'CLAUDE_API',
  'DEEP_SEEK_API',
];

export const sortMonitorTypesByDisplayOrder = (types: MonitorType[]): MonitorType[] => {
  const order = MONITOR_TYPE_DISPLAY_ORDER;
  return [...types].sort((a, b) => {
    const i = order.indexOf(a);
    const j = order.indexOf(b);
    return (i === -1 ? order.length : i) - (j === -1 ? order.length : j);
  });
};

export const getMonitorFullName = (monitorType: MonitorType, date?: Date) => {
  const checkDate = date ? new Date(date) : new Date();

  switch (monitorType) {
    case 'GPT_SEARCH_BROWSER':
    case 'GPT':
    case 'GPT_API_BROWSER': {
      const gpt5Date = new Date('2025-08-12T00:00:00.000Z');

      if (checkDate >= gpt5Date) {
        return 'GPT 5';
      } else {
        return 'GPT 4o';
      }
    }

    case 'GPT_API':
      return 'GPT 4o mini';

    case 'GPT_API_SEARCH_WEB':
      return 'GPT Search';

    case 'GEMINI_SEARCH_BROWSER':
    case 'GEMINI_API': {
      // Date-based logic for Gemini
      const geminiFlashDate = new Date('2025-02-08T00:00:00.000Z');
      const geminiFlash25Date = new Date('2025-07-08T14:08:03.419Z');
      const geminiFlash31Date = new Date('2026-02-19T00:00:00.000Z');
      const geminiFlash30Date = new Date('2025-12-07T00:00:00.000Z');

      if (checkDate >= geminiFlash31Date) {
        return 'Gemini Flash 3.1';
      } else if (checkDate >= geminiFlash30Date) {
        return 'Gemini Flash 3.0';
      } else if (checkDate >= geminiFlash25Date) {
        return 'Gemini Flash 2.5';
      } else if (checkDate >= geminiFlashDate) {
        return 'Gemini Flash 2.0';
      } else {
        return 'Gemini Flash 1.5';
      }
    }

    case 'GEMINI_API_BROWSER': {
      // Date-based logic for Gemini
      const geminiFlashPro25Date = new Date('2025-07-08T14:08:03.419Z');

      if (checkDate >= geminiFlashPro25Date) {
        return 'Gemini Flash 2.5 Pro';
      } else {
        return 'Gemini Flash 1.5 Pro';
      }
    }

    case 'CLAUDE_API': {
      // Date-based logic for Claude
      const claudeSonnet4Date = new Date('2025-07-08T14:08:03.419Z');
      const claudeSonnet46Date = new Date('2026-05-19T00:00:00.000Z');

      if (checkDate >= claudeSonnet46Date) {
        return 'Claude Sonnet 4.6';
      } else if (checkDate >= claudeSonnet4Date) {
        return 'Claude Sonnet 4';
      } else {
        return 'Claude 3.5 Sonnet';
      }
    }

    case 'DEEP_SEEK_API': {
      const deepSeekV4Date = new Date('2026-05-15T16:23:03.419Z');
      if (deepSeekV4Date >= deepSeekV4Date) {
        return 'DeepSeek V4';
      } else {
        return 'DeepSeek R1';
      }
    }

    case 'PERPLEXITY_SEARCH_BROWSER':
    case 'PERPLEXITY_API':
      return 'Perplexity Sonar Pro';

    case 'GROK_API':
    case 'GROK_SEARCH_BROWSER':
      return 'Grok';

    case 'AI_OVERVIEW_NEW':
      return 'AI Overview';

    case 'AI_MODE':
    case 'AI_OVERVIEW':
      return 'AI Mode';

    case 'COPILOT_SEARCH_BROWSER':
      return 'Copilot';

    default:
      return '';
  }
};

export const getMonitorName = (monitorType: MonitorType) => {
  switch (monitorType) {
    case 'GPT':
    case 'GPT_API_BROWSER':
    case 'GPT_API_SEARCH_WEB':
    case 'GPT_SEARCH_BROWSER':
      return 'GPT';

    case 'GPT_API':
      return 'GPT mini';

    case 'GEMINI_SEARCH_BROWSER':
    case 'GEMINI_API':
      return 'Gemini';

    case 'GEMINI_API_BROWSER':
      return 'Gemini Pro';

    case 'CLAUDE_API':
      return 'Claude';

    case 'DEEP_SEEK_API':
      return 'DeepSeek';

    case 'PERPLEXITY_SEARCH_BROWSER':
    case 'PERPLEXITY_API':
      return 'Perplexity';

    case 'GROK_API':
    case 'GROK_SEARCH_BROWSER':
      return 'Grok';

    case 'AI_OVERVIEW_NEW':
      return 'AI Overview';

    case 'AI_MODE':
    case 'AI_OVERVIEW':
      return 'AI Mode';

    case 'COPILOT_SEARCH_BROWSER':
      return 'Copilot';

    default:
      return '';
  }
};

export const getMonitorTypesForChatType = (chatType: ChatType): MonitorType[] => {
  switch (chatType) {
    case 'GEMINI':
      return ['GEMINI_API', 'GEMINI_API_BROWSER'];

    case 'CLAUDE':
      return ['CLAUDE_API'];

    case 'DEEP_SEEK':
      return ['DEEP_SEEK_API'];

    case 'PERPLEXITY':
      return ['PERPLEXITY_API'];

    case 'PERPLEXITY_BROWSER':
      return ['PERPLEXITY_SEARCH_BROWSER'];

    case 'GROK':
      return ['GROK_API'];

    case 'GROK_BROWSER':
      return ['GROK_SEARCH_BROWSER'];

    case 'AI_OVERVIEW_BROWSER':
      return ['AI_OVERVIEW'];

    case 'AI_MODE_BROWSER':
      return ['AI_MODE'];

    case 'AI_OVERVIEW_NEW_BROWSER':
      return ['AI_OVERVIEW_NEW'];

    case 'GEMINI_BROWSER':
      return ['GEMINI_SEARCH_BROWSER'];

    case 'COPILOT_BROWSER':
      return ['COPILOT_SEARCH_BROWSER'];

    case 'GPT_SEARCH':
      return ['GPT_API_SEARCH_WEB'];

    case 'GPT_BROWSER':
      return ['GPT_SEARCH_BROWSER'];

    case 'GPT':
    default:
      return ['GPT', 'GPT_API', 'GPT_API_BROWSER'];
  }
};

/**
 * Maps canShowModel keys to all ChatType values that represent that model
 * in MonitorPromptsKeywordsStatsResponse.chatType.
 */
export const MODEL_KEY_TO_CHAT_TYPES: Array<{ key: string; chatTypes: ChatType[] }> = [
  { key: 'GPT', chatTypes: ['GPT', 'GPT_BROWSER', 'GPT_SEARCH'] },
  { key: 'CLAUDE', chatTypes: ['CLAUDE'] },
  { key: 'GEMINI', chatTypes: ['GEMINI', 'GEMINI_BROWSER'] },
  { key: 'DEEP_SEEK', chatTypes: ['DEEP_SEEK'] },
  { key: 'PERPLEXITY', chatTypes: ['PERPLEXITY', 'PERPLEXITY_BROWSER'] },
  { key: 'COPILOT', chatTypes: ['COPILOT_BROWSER'] },
  { key: 'GROK', chatTypes: ['GROK', 'GROK_BROWSER'] },
  { key: 'AI_OVERVIEW', chatTypes: ['AI_OVERVIEW_NEW_BROWSER'] },
  { key: 'AI_MODE', chatTypes: ['AI_MODE_BROWSER', 'AI_OVERVIEW_BROWSER'] },
];

/**
 * Builds a Set of ChatType values allowed for the given account features.
 */
export const buildAllowedChatTypes = (features: string[]): Set<string> => {
  const allowed = new Set<string>();
  for (const { key, chatTypes } of MODEL_KEY_TO_CHAT_TYPES) {
    if (canShowModel(key, features)) {
      chatTypes.forEach((ct) => allowed.add(ct));
    }
  }
  return allowed;
};

/**
 * Expands selected ChatType values (from dropdown options) to include
 * all related ChatType variants for stats filtering.
 * E.g. if 'GPT' is selected, also includes 'GPT_BROWSER' and 'GPT_SEARCH'.
 */
/**
 * Checks if a canShowModel key (e.g. 'GPT', 'AI_OVERVIEW_NEW') has any of its
 * ChatType variants present in the selectedChatTypes array.
 */
export const isModelKeySelected = (modelKey: string, selectedChatTypes: ChatType[]): boolean => {
  const lookupKey = modelKey === 'AI_OVERVIEW_NEW' ? 'AI_OVERVIEW' : modelKey;
  const entry = MODEL_KEY_TO_CHAT_TYPES.find((m) => m.key === lookupKey);
  if (!entry) return false;
  return entry.chatTypes.some((ct) => selectedChatTypes.includes(ct as ChatType));
};

export const expandSelectedChatTypes = (selected: ChatType[]): Set<string> => {
  const expanded = new Set<string>();
  for (const { chatTypes } of MODEL_KEY_TO_CHAT_TYPES) {
    if (chatTypes.some((ct) => selected.includes(ct as ChatType))) {
      chatTypes.forEach((ct) => expanded.add(ct));
    }
  }
  return expanded;
};
